import { FORM_DIRTY_EVENT, type FormDirtyEventDetail } from './events';

export const GUARDED_FORM_SELECTOR = 'form.aform, form[data-unsaved-guard]';

const INTERACTION_EVENTS = ['input', 'change', 'keydown', 'pointerdown'] as const;

type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

// EditorJS only writes its content into this input while submitting; it reports edits via FORM_DIRTY_EVENT.
const EDITORJS_INPUT_ID = 'editorjs-value';

// EditorJS lives inside the form and injects its own inputs there (toolbar search, tool settings, uploads).
const EDITOR_SELECTOR = '#editorjs, .codex-editor';

const isSnapshotField = (field: FormField): boolean => field.id !== EDITORJS_INPUT_ID
  && field.name !== ''
  && field.closest(EDITOR_SELECTOR) === null;

const serializeForm = (form: HTMLFormElement): string => Array
  .from(form.querySelectorAll<FormField>('input, select, textarea'))
  .filter(isSnapshotField)
  .map((field) => {
    if (field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio')) {
      return `${field.name}:${field.checked ? 1 : 0}`;
    }

    return `${field.name}:${field.value}`;
  })
  .join('\n');

/**
 * Warns before unsaved CMS form edits are lost.
 *
 * The admin is a classic multi-page app, so menu links, the Close button and browser
 * back/forward all unload the document — `beforeunload` covers every one of them.
 *
 * Legacy widgets (the RTF toolbar, gallery pickers, ...) assign to `field.value` without
 * emitting events, so the decision is made by comparing the form against a snapshot taken
 * on load. That comparison runs once, when the page is actually being left.
 */
export default class UnsavedChangesGuard {
  private forms: HTMLFormElement[] = [];

  private snapshot = '';

  private touched = false;

  /** Set by editors whose content lives outside the DOM, so a snapshot cannot see it. */
  private editorDirty = false;

  public init(): void {
    this.forms = Array.from(document.querySelectorAll<HTMLFormElement>(GUARDED_FORM_SELECTOR));

    if (this.forms.length === 0) {
      return;
    }

    this.takeSnapshot();

    window.addEventListener('load', this.handleWindowLoad);
    INTERACTION_EVENTS.forEach((name) => document.addEventListener(name, this.handleInteraction, true));
    document.addEventListener(FORM_DIRTY_EVENT, this.markEditorDirty);
    document.addEventListener('submit', this.handleSubmit, true);
  }

  public hasUnsavedChanges(): boolean {
    return this.editorDirty || this.serialize() !== this.snapshot;
  }

  public reset = (): void => {
    this.editorDirty = false;
    this.takeSnapshot();
  };

  public destroy(): void {
    window.removeEventListener('load', this.handleWindowLoad);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    INTERACTION_EVENTS.forEach((name) => document.removeEventListener(name, this.handleInteraction, true));
    document.removeEventListener(FORM_DIRTY_EVENT, this.markEditorDirty);
    document.removeEventListener('submit', this.handleSubmit, true);
    this.forms = [];
    this.touched = false;
    this.editorDirty = false;
  }

  private serialize(): string {
    return this.forms.map(serializeForm).join('\u0000');
  }

  private takeSnapshot(): void {
    this.snapshot = this.serialize();
  }

  // Widgets that initialise late would otherwise look like edits.
  private handleWindowLoad = (): void => {
    if (!this.touched) {
      this.takeSnapshot();
    }
  };

  private handleInteraction = (event: Event): void => {
    const target = event.target as Element | null;

    if (typeof target?.closest !== 'function' || !target.closest(GUARDED_FORM_SELECTOR)) {
      return;
    }

    this.startGuarding();
  };

  private markEditorDirty = (event: Event): void => {
    const { dirty = true } = (event as CustomEvent<FormDirtyEventDetail>).detail ?? {};

    this.editorDirty = dirty;

    if (dirty) {
      this.startGuarding();
    }
  };

  private startGuarding(): void {
    if (this.touched) {
      return;
    }

    this.touched = true;
    INTERACTION_EVENTS.forEach((name) => document.removeEventListener(name, this.handleInteraction, true));
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  private stopGuarding(): void {
    if (!this.touched) {
      return;
    }

    this.touched = false;
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    INTERACTION_EVENTS.forEach((name) => document.addEventListener(name, this.handleInteraction, true));
  }

  private handleSubmit = (event: Event): void => {
    const form = event.target as Element | null;

    if (typeof form?.matches !== 'function' || !form.matches(GUARDED_FORM_SELECTOR)) {
      return;
    }

    this.reset();
    this.stopGuarding();
  };

  private handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (!this.hasUnsavedChanges()) {
      return;
    }

    event.preventDefault();
    event.returnValue = '';
  };
}
